export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  image: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
}

export const blogPosts: BlogPost[] = [
  {
    slug: "guia-comprar-patinete-electrico-segunda-mano",
    title: "Guía completa para comprar un patinete eléctrico de segunda mano",
    excerpt: "Todo lo que necesitas saber antes de comprar un patinete eléctrico usado: qué revisar, preguntas clave y errores comunes que debes evitar.",
    content: `
## ¿Por qué comprar un patinete eléctrico de segunda mano?

Comprar un patinete eléctrico de segunda mano es una excelente manera de ahorrar dinero sin sacrificar calidad. Muchos patinetes usados están en perfecto estado y cuestan entre un 30% y un 50% menos que uno nuevo.

## Qué revisar antes de comprar

### 1. Estado de la batería
La batería es el componente más caro de un patinete eléctrico. Pregunta siempre por los ciclos de carga y comprueba que mantiene al menos el 80% de su capacidad original. Una batería degradada puede costar entre 100€ y 300€ reemplazarla.

### 2. Ruedas y frenos
Revisa el desgaste de las ruedas. Si son neumáticas, comprueba que no tengan pinchazos. Los frenos deben responder inmediatamente y sin ruidos extraños.

### 3. Motor y electrónica
Enciende el patinete y pruébalo. Escucha si hay ruidos anómalos en el motor. Comprueba que el display funciona correctamente y que todas las luces se encienden.

### 4. Estructura y chasis
Busca grietas, golpes o soldaduras. El mecanismo de plegado debe funcionar sin holguras. Una estructura dañada puede ser peligrosa.

## Preguntas clave al vendedor

- ¿Cuántos kilómetros tiene recorridos?
- ¿Ha sufrido algún accidente o caída fuerte?
- ¿Tiene la factura original y garantía vigente?
- ¿Por qué lo vende?
- ¿Tiene homologación DGT?

## Errores comunes

1. **No probar el patinete**: Siempre pide una prueba antes de pagar.
2. **Ignorar la homologación**: En España, los patinetes deben cumplir la normativa DGT.
3. **Pagar sin recibo**: Siempre solicita un comprobante de la transacción.
4. **No verificar el modelo**: Comprueba el número de serie para asegurarte de que no es robado.

## Precio justo de mercado

Un patinete de gama media como el Xiaomi Scooter 4 Pro nuevo cuesta unos 600€. De segunda mano, en buen estado, debería rondar los 350-450€. Si el precio es demasiado bajo, desconfía.
    `,
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=800&h=450&fit=crop",
    category: "Guías",
    author: "eScooter",
    date: "2026-02-20",
    readTime: "8 min",
  },
  {
    slug: "normativa-dgt-patinetes-electricos-2026",
    title: "Normativa DGT 2026: Todo sobre la regulación de patinetes eléctricos",
    excerpt: "La normativa DGT para Vehículos de Movilidad Personal actualizada. Homologación, seguros, dónde circular y multas que debes conocer.",
    content: `
## Normativa actual de la DGT para patinetes eléctricos

Desde la entrada en vigor del Real Decreto 970/2020, los patinetes eléctricos están clasificados como Vehículos de Movilidad Personal (VMP). En 2026, la regulación se ha reforzado con nuevos requisitos.

## Requisitos obligatorios

### Homologación
Todos los patinetes eléctricos deben contar con un certificado de circulación expedido por un organismo autorizado. Sin homologación, circular con un patinete es ilegal y puede acarrear multas de hasta 500€.

### Características técnicas permitidas
- **Velocidad máxima**: 25 km/h
- **Potencia máxima del motor**: 500W (nominal)
- **Peso máximo**: No hay límite específico, pero debe cumplir normas de seguridad
- **Iluminación**: Obligatoria delantera y trasera
- **Reflectantes**: Laterales obligatorios
- **Timbre**: Obligatorio

### Dónde se puede circular
- ✅ Carriles bici
- ✅ Vías ciclistas
- ✅ Calzada en vías con límite ≤ 30 km/h
- ❌ Aceras (prohibido siempre)
- ❌ Vías interurbanas
- ❌ Autopistas y autovías

## Multas más comunes

| Infracción | Multa |
|---|---|
| Circular por acera | 200€ |
| Sin homologación | 500€ |
| Sin luces | 200€ |
| Uso de auriculares | 200€ |
| Circular bajo efectos del alcohol | 500-1.000€ |
| Llevar pasajero | 100€ |

## Seguro obligatorio

Aunque actualmente no es obligatorio un seguro de responsabilidad civil a nivel estatal, muchas ciudades lo exigen. En Barcelona y Valencia ya es obligatorio. Recomendamos siempre contratar uno: cuestan entre 50€ y 100€ al año.

## ¿Qué pasa si compro un patinete sin homologar?

Si compras un patinete sin certificado de circulación, no podrás circular legalmente por vías públicas. En eScooter, todos los anuncios indican si el patinete tiene homologación DGT, para que compres con total seguridad.
    `,
    image: "https://images.unsplash.com/photo-1604868804079-a4010a5b3820?w=800&h=450&fit=crop",
    category: "Normativa",
    author: "eScooter",
    date: "2026-02-15",
    readTime: "6 min",
  },
  {
    slug: "mejores-patinetes-electricos-calidad-precio",
    title: "Los 5 mejores patinetes eléctricos en calidad-precio de 2026",
    excerpt: "Analizamos los patinetes eléctricos con mejor relación calidad-precio del mercado. Comparativa actualizada con precios y características.",
    content: `
## Los patinetes eléctricos más recomendados de 2026

Hemos analizado decenas de modelos para seleccionar los 5 patinetes con mejor relación calidad-precio del mercado actual.

## 1. Xiaomi Electric Scooter 4 Pro (2ª Gen)

**Precio nuevo**: ~599€ | **Precio segunda mano**: ~380€

El Xiaomi 4 Pro sigue siendo el rey de la relación calidad-precio. Con 25 km de autonomía real, motor de 350W y ruedas de 10 pulgadas, es ideal para desplazamientos urbanos diarios.

**Pros**: Fiable, repuestos baratos, app completa
**Contras**: Peso (16,5 kg), sin suspensión trasera

## 2. Segway Ninebot MAX G2

**Precio nuevo**: ~799€ | **Precio segunda mano**: ~500€

El tanque de los patinetes urbanos. Con 70 km de autonomía (la mayor del mercado), suspensión doble y neumáticos de 10,2", es perfecto para trayectos largos.

**Pros**: Autonomía brutal, muy cómodo, resistente al agua
**Contras**: Pesado (24,3 kg), precio elevado

## 3. Cecotec Bongo Serie Z

**Precio nuevo**: ~449€ | **Precio segunda mano**: ~280€

Una opción nacional con muy buen rendimiento. Motor de 400W, batería extraíble y ruedas antipinchazos de 10".

**Pros**: Batería extraíble, buen precio, marca española
**Contras**: App mejorable, menos accesorios disponibles

## 4. Smartgyro Rockway

**Precio nuevo**: ~399€ | **Precio segunda mano**: ~250€

El más compacto y ligero de la lista. Con solo 12 kg, es perfecto para combinar con transporte público.

**Pros**: Ultraligero, plegado rápido, buen precio
**Contras**: Autonomía limitada (20 km), ruedas pequeñas (8,5")

## 5. NIU KQi3 Pro

**Precio nuevo**: ~549€ | **Precio segunda mano**: ~350€

Diseño premium a precio competitivo. Motor de 350W, autonomía de 40 km y sistema de frenado regenerativo avanzado.

**Pros**: Diseño elegante, buena autonomía, freno regenerativo
**Contras**: Repuestos menos accesibles en España

## Tabla comparativa

| Modelo | Autonomía | Peso | Motor | Precio nuevo |
|---|---|---|---|---|
| Xiaomi 4 Pro | 25 km | 16,5 kg | 350W | 599€ |
| Ninebot MAX G2 | 70 km | 24,3 kg | 450W | 799€ |
| Bongo Serie Z | 30 km | 18 kg | 400W | 449€ |
| Smartgyro Rockway | 20 km | 12 kg | 300W | 399€ |
| NIU KQi3 Pro | 40 km | 17,5 kg | 350W | 549€ |

## ¿Dónde encontrar estos patinetes de segunda mano?

En **eScooter** puedes encontrar todos estos modelos de segunda mano a precios increíbles. Todos nuestros anuncios están verificados y los vendedores son usuarios reales.
    `,
    image: "https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=800&h=450&fit=crop",
    category: "Comparativas",
    author: "eScooter",
    date: "2026-02-10",
    readTime: "10 min",
  },
  {
    slug: "mantenimiento-patinete-electrico",
    title: "Mantenimiento del patinete eléctrico: guía práctica para alargar su vida útil",
    excerpt: "Consejos esenciales de mantenimiento para que tu patinete eléctrico dure más años. Batería, ruedas, frenos y limpieza.",
    content: `
## Por qué el mantenimiento importa

Un patinete eléctrico bien cuidado puede durar más de 5 años. Un mantenimiento básico regular no solo alarga su vida útil, sino que mejora tu seguridad y el valor de reventa.

## Cuidado de la batería

La batería es el componente más delicado y caro. Sigue estos consejos:

- **No la descargues al 0%**: Intenta recargar cuando quede un 20-30%
- **No la dejes al 100% mucho tiempo**: Si no vas a usarlo en días, déjala al 60-80%
- **Evita temperaturas extremas**: No cargues por debajo de 0°C ni por encima de 40°C
- **Usa el cargador original**: Los cargadores genéricos pueden dañar las celdas
- **Carga completa mensual**: Una vez al mes, haz un ciclo completo para calibrar el BMS

## Ruedas y neumáticos

### Ruedas neumáticas
- Revisa la presión cada semana (normalmente 40-50 PSI)
- Inspecciona el desgaste del dibujo
- Cambia las cámaras si tienen pinchazos frecuentes
- Considera líquido antipinchazos

### Ruedas sólidas
- Menos mantenimiento, pero revisa el desgaste
- Si vibran mucho, puede ser hora de cambiarlas

## Frenos

- **Freno de disco**: Ajusta las pastillas cada 500 km. Cámbialas cuando tengan menos de 1 mm
- **Freno eléctrico/regenerativo**: No requiere mantenimiento mecánico
- **Freno de tambor**: Revisa cada 1.000 km

## Limpieza

- Usa un trapo húmedo, nunca chorros de agua a presión
- Limpia la suciedad de las articulaciones de plegado
- Aplica lubricante en las partes móviles cada mes
- Seca bien después de circular bajo lluvia

## Cuándo llevar al taller

Lleva tu patinete a un taller especializado si:
- La batería dura menos de la mitad de lo normal
- Hay ruidos extraños en el motor
- Los frenos no responden bien tras ajustarlos
- El chasis tiene grietas o deformaciones
- Los errores del display son persistentes

## Calendario de mantenimiento

| Frecuencia | Tarea |
|---|---|
| Semanal | Revisar presión ruedas, comprobar frenos |
| Mensual | Limpiar, lubricar partes móviles, ciclo batería completo |
| Trimestral | Revisar tornillería, inspeccionar cables |
| Semestral | Revisión completa en taller |
    `,
    image: "https://images.unsplash.com/photo-1600132806608-5730b1043ab1?w=800&h=450&fit=crop",
    category: "Mantenimiento",
    author: "eScooter",
    date: "2026-02-05",
    readTime: "7 min",
  },
  {
    slug: "vender-patinete-electrico-consejos",
    title: "Cómo vender tu patinete eléctrico rápido y al mejor precio",
    excerpt: "Trucos y consejos para vender tu patinete eléctrico usado rápidamente. Fotos, descripción, precio y estrategia de venta.",
    content: `
## Prepara tu patinete para la venta

Antes de publicar tu anuncio, dedica una hora a preparar tu patinete:

1. **Limpieza a fondo**: Un patinete limpio se vende hasta un 20% más caro
2. **Reparaciones menores**: Arregla pequeños desperfectos (cambiar puños, apretar tornillos)
3. **Carga completa**: Muéstralo con la batería al 100%

## Cómo fijar el precio correcto

### Fórmula orientativa
- **Menos de 1 año**: 70-80% del precio nuevo
- **1-2 años**: 50-65% del precio nuevo
- **Más de 2 años**: 35-50% del precio nuevo

### Factores que aumentan el precio
- Homologación DGT vigente (+10-15%)
- Factura y garantía original (+5-10%)
- Pocos kilómetros recorridos
- Accesorios incluidos (candado, soporte móvil, etc.)

## Fotos que venden

Las fotos son lo primero que ve el comprador. En eScooter puedes subir hasta 4:

1. **Foto general**: El patinete completo, de perfil, sobre fondo limpio
2. **Detalle frontal**: Display, manillar y luces
3. **Ruedas y frenos**: Muestra el estado del desgaste
4. **Número de serie**: Genera confianza

### Tips para fotos
- Luz natural, preferiblemente exterior
- Fondo neutro (pared blanca, exterior limpio)
- Sin filtros ni exceso de edición
- Resolución alta

## Escribe una descripción ganadora

Una buena descripción debe incluir:
- Marca y modelo exacto
- Año de compra
- Kilómetros recorridos (si lo sabes)
- Estado de la batería
- Accesorios incluidos
- Motivo de la venta
- Si tiene homologación DGT

## Gestión de interesados

- Responde rápido a los mensajes (el chat de eScooter te lo pone fácil)
- Sé honesto sobre el estado del patinete
- Ofrece una prueba al comprador
- Acuerda un punto de encuentro público y seguro
- Acepta pagos seguros (transferencia o efectivo en persona)

## Publica en eScooter

En eScooter tu anuncio llega directamente a compradores interesados en patinetes eléctricos. Es un marketplace especializado, lo que significa menos ruido y más contactos de calidad.
    `,
    image: "https://images.unsplash.com/photo-1605144190700-5af0ed04b128?w=800&h=450&fit=crop",
    category: "Consejos",
    author: "eScooter",
    date: "2026-01-28",
    readTime: "6 min",
  },
];
